import {
  FC,
  SyntheticEvent,
  createContext,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { useFormikContext } from 'formik';

import { IntegrationTypeInfo, CustomAutocomplete } from '~components/index';
import { TableColumn } from '~components/Table/types';
import { AdminIntegration } from '~services/admin/integration/types';
import {
  SelectRowsHandlerOnSelectAll,
  SelectRowsHandlerOnSelect,
} from '~hooks/useDataTableSelectedProps';
import { MERCADO_LIBRE_SHIPPING_OPTIONS } from '~constants/integrationsProviders';
import { SelectOption } from '~globals/types/commons';
import { hasError } from '~utils/formHelpers';

// eslint-disable-next-line max-len
import { useDialogImportOrdersFromIntegrationContext } from './DialogImportOrdersFromIntegrationContext';
import {
  FIELDS_NAME,
  getInitialValues,
  toIntegrationPathField,
  isMeliIntegration,
} from './utils';
import { DialogImportOrdersFromIntegrationTableChip } from './styles';

interface DialogImportOrdersFromIntegrationTableContextSelectableProps {
  selectedIds: Array<number | string>;
  onSelectAll: SelectRowsHandlerOnSelectAll<AdminIntegration>;
  onSelect: SelectRowsHandlerOnSelect<AdminIntegration>;
}

interface DialogImportOrdersFromIntegrationTableContextValue {
  columns: TableColumn<AdminIntegration>[];
  selectableProps: DialogImportOrdersFromIntegrationTableContextSelectableProps;
}

const DialogImportOrdersFromIntegrationTableContext =
  createContext<DialogImportOrdersFromIntegrationTableContextValue>({
    columns: [],
    selectableProps: {
      selectedIds: [],
      onSelectAll: () => {},
      onSelect: () => {},
    },
  });

export const useDialogImportOrdersFromIntegrationTableContext =
  (): DialogImportOrdersFromIntegrationTableContextValue =>
    useContext(DialogImportOrdersFromIntegrationTableContext);

const DialogImportOrdersFromIntegrationTableProvider: FC<
  Pick<DialogImportOrdersFromIntegrationTableContextValue, 'selectableProps'>
> = ({
  children,
  selectableProps: {
    selectedIds,
    onSelectAll: handleSelectAll,
    onSelect: handleSelect,
  },
}) => {
  const {
    currentIntegrations: { list },
  } = useDialogImportOrdersFromIntegrationContext();

  const {
    values,
    setFieldValue,
    setFieldTouched,
    touched,
    errors,
    validateForm,
  } = useFormikContext<ReturnType<typeof getInitialValues>>();

  const getDeliveryTypeValue = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (rowIndex: number): any => {
      const currentDeliveryTypesValues =
        values[FIELDS_NAME.INTEGRATIONS][rowIndex][
          FIELDS_NAME.INTEGRATION_DELIVERY_TYPE
        ];

      return currentDeliveryTypesValues.map((deliveryType) =>
        MERCADO_LIBRE_SHIPPING_OPTIONS.find(
          (item) => item.value === deliveryType,
        ),
      );
    },
    [values],
  );

  const handleChangeDeliveryType = useCallback(
    (rowIndex: number) =>
      (_e: SyntheticEvent<Element, Event>, vals: SelectOption[]) => {
        const currentDeliveryTypes = vals.map((value) => value.value);

        setFieldValue(
          toIntegrationPathField(
            rowIndex,
            FIELDS_NAME.INTEGRATION_DELIVERY_TYPE,
          ),
          currentDeliveryTypes,
        );
      },
    [setFieldValue],
  );

  const handleBlurDeliveryType = useCallback(
    (rowIndex: number) => () => {
      setFieldTouched(
        toIntegrationPathField(rowIndex, FIELDS_NAME.INTEGRATION_DELIVERY_TYPE),
        true,
      );
    },
    [setFieldTouched],
  );

  const getErrorDeliveryType = useCallback(
    (rowIndex: number) =>
      hasError(
        touched,
        errors,
        toIntegrationPathField(rowIndex, FIELDS_NAME.INTEGRATION_DELIVERY_TYPE),
      ),
    [errors, touched],
  );

  const isDisabledRow = useCallback(
    (rowIndex: number) =>
      !values[FIELDS_NAME.INTEGRATIONS][rowIndex][
        FIELDS_NAME.INTEGRATION_CHECKED
      ],
    [values],
  );

  const columns = useMemo<TableColumn<AdminIntegration>[]>(
    () => [
      {
        dataField: 'description',
        text: 'Tienda',
        classes: {
          cell: 'truncated-two-lines',
        },
        formatters: {
          cell: (cell) => (
            <span>
              <b>{cell}</b>
            </span>
          ),
        },
      },
      {
        dataField: 'integrationTypeId',
        text: 'Integración',
        formatters: {
          cell: (_cell, row) => (
            <IntegrationTypeInfo integrationType={row.integrationTypeId} />
          ),
        },
      },
      {
        dataField: 'companyIntegrationId',
        isDummyField: true,
        text: 'Tipo de envío',
        formatters: {
          cell: (_cell, row, rowIndex) => {
            if (!isMeliIntegration(row.integrationTypeId)) {
              return null;
            }

            return (
              <CustomAutocomplete
                multiple
                disableCloseOnSelect
                filterSelectedOptions
                disableClearable
                label="Seleccionar el tipo de envío"
                options={MERCADO_LIBRE_SHIPPING_OPTIONS}
                value={getDeliveryTypeValue(rowIndex)}
                onChange={handleChangeDeliveryType(rowIndex)}
                onBlur={handleBlurDeliveryType(rowIndex)}
                error={getErrorDeliveryType(rowIndex)}
                disabled={isDisabledRow(rowIndex)}
                limitTags={1}
                renderTags={(vals, getTagsProps) =>
                  vals.map((val, index) => (
                    // eslint-disable-next-line react/jsx-key
                    <DialogImportOrdersFromIntegrationTableChip
                      label={val.label as string}
                      size="small"
                      {...getTagsProps({ index })}
                    />
                  ))
                }
                getOptionLabel={(option) => option.label as string}
              />
            );
          },
        },
        style: {
          header: { width: 300 },
        },
      },
    ],
    [
      getDeliveryTypeValue,
      handleChangeDeliveryType,
      handleBlurDeliveryType,
      getErrorDeliveryType,
      isDisabledRow,
    ],
  );

  const onSelectAll = useCallback<
    SelectRowsHandlerOnSelectAll<AdminIntegration>
  >(
    async (isSelected, rows, event) => {
      const rowsData = isSelected ? rows : list;

      await Promise.all(
        rowsData.map((_row, rowIndex) => {
          return setFieldValue(
            toIntegrationPathField(rowIndex, FIELDS_NAME.INTEGRATION_CHECKED),
            isSelected,
            false,
          );
        }),
      );

      await validateForm();

      handleSelectAll(isSelected, rows, event as SyntheticEvent);
    },
    [list, validateForm, handleSelectAll, setFieldValue],
  );

  const onSelect = useCallback<SelectRowsHandlerOnSelect<AdminIntegration>>(
    (row, isSelected, rowIndex, event) => {
      setFieldValue(
        toIntegrationPathField(rowIndex, FIELDS_NAME.INTEGRATION_CHECKED),
        isSelected,
      );

      handleSelect(row, isSelected, rowIndex, event as SyntheticEvent);
    },
    [setFieldValue, handleSelect],
  );

  return (
    <DialogImportOrdersFromIntegrationTableContext.Provider
      value={{
        columns: columns,
        selectableProps: {
          selectedIds,
          onSelectAll,
          onSelect,
        },
      }}
    >
      {children}
    </DialogImportOrdersFromIntegrationTableContext.Provider>
  );
};

export default DialogImportOrdersFromIntegrationTableProvider;
